/**
 * CALIBRAÇÃO COM DADOS — o b de cada item estimado a partir de respostas reais.
 *
 * Até aqui, a dificuldade de cada item é um ENVELOPE A PRIORI, montado das fontes de
 * dificuldade. Com respostas de verdade, dá para estimar b pelo modelo de Rasch (o 1PL):
 *
 *     P(acerto) = 1 / (1 + e^−(θ − b))
 *
 * Estimação por máxima verossimilhança conjunta (JML), por Newton-Raphson alternando
 * pessoas e itens, com três cuidados conhecidos:
 *  - escores extremos (tudo certo, tudo errado) não têm estimativa finita: o escore é
 *    puxado 0,3 para dentro;
 *  - o passo de Newton é limitado a ±1 e os valores a ±6, para a iteração não explodir
 *    com poucos dados;
 *  - o viés conhecido da JML é corrigido pelo fator (L − 1)/L de Wright.
 *
 * A escala é identificada pela média dos b: zero. Quem compara com o a priori desloca
 * uma escala para a média da outra.
 *
 * É calibração de TEMPLATE: as respostas de variantes diferentes do mesmo item-modelo
 * entram juntas. Isso é o b_template do plano; o σ² entre variantes fica para quando
 * houver dados para ele.
 */

export interface RespostaDicotomica {
  readonly pessoa: string;
  readonly item: string;
  readonly correto: boolean;
}

export interface ItemCalibrado {
  readonly item: string;
  readonly b: number;
  /** Erro-padrão de b: 1/√(informação). */
  readonly ep: number;
  readonly n: number;
  readonly acertos: number;
}

export interface Calibracao {
  readonly itens: readonly ItemCalibrado[];
  readonly pessoas: ReadonlyMap<string, number>;
  readonly iteracoes: number;
  readonly convergiu: boolean;
}

const logit = (p: number): number => Math.log(p / (1 - p));
const prob = (theta: number, b: number): number => 1 / (1 + Math.exp(-(theta - b)));
const limitar = (v: number, m: number): number => Math.max(-m, Math.min(m, v));

/** Escore puxado para dentro, para que tudo certo ou tudo errado tenha estimativa finita. */
const ajustado = (acertos: number, n: number): number => Math.min(Math.max(acertos, 0.3), n - 0.3);

export function calibrarRasch(
  resps: readonly RespostaDicotomica[],
  maxIter = 500,
  tol = 1e-4,
): Calibracao {
  const pessoasIds = [...new Set(resps.map((r) => r.pessoa))];
  const itensIds = [...new Set(resps.map((r) => r.item))];
  const ip = new Map(pessoasIds.map((p, i) => [p, i]));
  const ii = new Map(itensIds.map((x, i) => [x, i]));
  const obs = resps.map((r) => ({
    p: ip.get(r.pessoa) as number,
    i: ii.get(r.item) as number,
    x: r.correto ? 1 : 0,
  }));

  const nP = new Array<number>(pessoasIds.length).fill(0);
  const sP = new Array<number>(pessoasIds.length).fill(0);
  const nI = new Array<number>(itensIds.length).fill(0);
  const sI = new Array<number>(itensIds.length).fill(0);
  for (const o of obs) {
    nP[o.p]! += 1;
    sP[o.p]! += o.x;
    nI[o.i]! += 1;
    sI[o.i]! += o.x;
  }
  const alvoP = nP.map((n, k) => ajustado(sP[k] as number, n));
  const alvoI = nI.map((n, k) => ajustado(sI[k] as number, n));

  const theta = alvoP.map((s, k) => limitar(logit(s / (nP[k] as number)), 6));
  const b = alvoI.map((s, k) => limitar(-logit(s / (nI[k] as number)), 6));

  let iteracoes = 0;
  let convergiu = false;
  for (; iteracoes < maxIter; iteracoes++) {
    // Pessoas, dados os b: independentes entre si, cada uma com alguns passos de Newton.
    // θ ← θ + (O − E) / Σ P(1 − P)
    for (let rep = 0; rep < 3; rep++) {
      const eP = new Array<number>(pessoasIds.length).fill(0);
      const vP = new Array<number>(pessoasIds.length).fill(0);
      for (const o of obs) {
        const P = prob(theta[o.p] as number, b[o.i] as number);
        eP[o.p]! += P;
        vP[o.p]! += P * (1 - P);
      }
      for (let k = 0; k < theta.length; k++) {
        const passo = limitar(
          ((alvoP[k] as number) - (eP[k] as number)) / Math.max(vP[k] as number, 1e-9),
          1,
        );
        theta[k] = limitar((theta[k] as number) + passo, 6);
      }
    }
    // Itens, dados os θ: b ← b + (E − O) / Σ P(1 − P), e a média dos b volta a zero.
    const antes = [...b];
    const eI = new Array<number>(itensIds.length).fill(0);
    const vI = new Array<number>(itensIds.length).fill(0);
    for (const o of obs) {
      const P = prob(theta[o.p] as number, b[o.i] as number);
      eI[o.i]! += P;
      vI[o.i]! += P * (1 - P);
    }
    for (let k = 0; k < b.length; k++) {
      const passo = limitar(
        ((eI[k] as number) - (alvoI[k] as number)) / Math.max(vI[k] as number, 1e-9),
        1,
      );
      b[k] = limitar((b[k] as number) + passo, 6);
    }
    const media = b.reduce((s, v) => s + v, 0) / b.length;
    for (let k = 0; k < b.length; k++) b[k] = (b[k] as number) - media;
    // Convergência medida no que interessa: quanto os b ainda mudam.
    const maior = Math.max(...b.map((v, k) => Math.abs(v - (antes[k] as number))));
    if (maior < tol) {
      convergiu = true;
      break;
    }
  }

  const L = itensIds.length;
  const wright = L > 1 ? (L - 1) / L : 1;
  const info = new Array<number>(L).fill(0);
  for (const o of obs) {
    const P = prob(theta[o.p] as number, b[o.i] as number);
    info[o.i]! += P * (1 - P);
  }
  const itens = itensIds.map((item, k) => ({
    item,
    b: (b[k] as number) * wright,
    ep: 1 / Math.sqrt(Math.max(info[k] as number, 1e-9)),
    n: nI[k] as number,
    acertos: sI[k] as number,
  }));
  return {
    itens,
    pessoas: new Map(pessoasIds.map((p, k) => [p, theta[k] as number])),
    iteracoes,
    convergiu,
  };
}
